import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Toolbar, useMediaQuery, Button, Grid, Paper, List, ListItem, ListItemIcon, Stack, Container, Avatar, AvatarGroup, Chip, Card, CardContent, Badge } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from "framer-motion";
import {
  Flag,
  Search,
  Star,
  Chat,
  Paid,
  People,
  Handshake,
  TrendingUp,
  Insights,
  ConnectWithoutContact,
  GroupAdd,
  Campaign,
  Hub
} from "@mui/icons-material";
import HomeHeader from "../../../Components/HomeHeader";
import HomeFooter from "../../../Components/HomeFooter";
import routes from "../../../Config/routes";
import collaborateImage from "../../../Components/globalAssets/collaborate-img.png";

const bounceIn = { hidden: { opacity: 0, scale: 0.3 }, visible: { opacity: 1, scale: 1, transition: { type: "spring", bounce: 0.4 } } };
const slideInNetwork = { hidden: { opacity: 0, x: -60, rotate: -5 }, visible: { opacity: 1, x: 0, rotate: 0 } };
const fadeInStagger = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

const stats = [
  { value: "1000+", label: "Campaigns Launched", icon: <Campaign />, color: "#FF6B35" },
  { value: "1200+", label: "Creators Onboarded", icon: <GroupAdd />, color: "#F7931E" },
  { value: "98%", label: "Collaboration Success", icon: <Handshake />, color: "#FFD23F" },
  { value: "48h", label: "Avg Response Time", icon: <Chat />, color: "#06FFA5" }
];

const modules = [
  {
    icon: <Flag fontSize="large" />,
    title: "Blitz Campaigns",
    description: "Create and manage campaigns end-to-end: budgets, timelines, deliverables, approvals.",
    category: "Campaign Management",
    network: "Brand → Creator",
    color: "#FF6B35"
  },
  {
    icon: <Search fontSize="large" />,
    title: "Marketplace",
    description: "Browse, filter, and shortlist creators; view rich profiles and broadcast open briefs.",
    category: "Discovery",
    network: "Brand ↔ Platform",
    color: "#F7931E"
  },
  {
    icon: <Star fontSize="large" />,
    title: "Blitz Partnerships",
    description: "Promote top creators to Partner or Associate with one-click email-alias management.",
    category: "Relationship Building",
    network: "Long-term Partnership",
    color: "#FFD23F"
  },
  {
    icon: <Chat fontSize="large" />,
    title: "Blitz Chats",
    description: "In-app negotiation and direct deals with deliverable checklists and approvals.",
    category: "Communication",
    network: "Real-time Connection",
    color: "#06FFA5"
  },
  {
    icon: <Paid fontSize="large" />,
    title: "BlitzPay",
    description: "Trigger instant payouts, manage advances, and reconcile all transactions seamlessly.",
    category: "Financial Flow",
    network: "Brand → Creator",
    color: "#3BCEAC"
  }
];

const networkConnections = [
  { type: "Brand", count: 2400, color: "#FF6B35" },
  { type: "Creator", count: 1200, color: "#F7931E" },
  { type: "Agency", count: 340, color: "#FFD23F" }
];

export default function Collaborate() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const refs = useRef({});

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      refs.current[id]?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);

  const handleNavigate = () => navigate(routes.onboard);
  const handleNavigate1 = () => navigate(routes.help);


  const NetworkModuleCard = ({ icon, title, description, category, network, color }) => {
    // Determine if color is too light for white background (green shades)
    const isLightColor = color === "#06FFA5" || color === "#3BCEAC" || color === "#FFD23F";
    const textColor = isLightColor ? theme.palette.text.primary : color;
    const chipBgColor = isLightColor ? `${color}30` : `${color}20`;
    
    return (
    <Card
      component={motion.div}
      variants={fadeInStagger}
      whileHover={{ scale: 1.02, rotate: 1 }}
      sx={{
        height: "100%",
        borderRadius: 4,
        position: "relative",
        overflow: "hidden",
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        "&:hover": {
          boxShadow: "0 15px 35px rgba(0,0,0,0.1)",
          borderColor: theme.palette.primary.main,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: -2,
          right: -2,
          width: 80,
          height: 80,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
        }
      }}
    >
      <CardContent sx={{ p: { xs: 3, md: 4 }, height: "100%", position: "relative", zIndex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
              color: "#fff",
              mr: 3,
              boxShadow: `0 8px 25px ${color}40`,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: theme.palette.text.primary }}>
              {title}
            </Typography>
            <Chip
              label={category}
              size="small"
              sx={{
                bgcolor: chipBgColor,
                color: textColor,
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          </Box>
        </Box>
        
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3, lineHeight: 1.6 }}>
          {description}
        </Typography>
        
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            borderRadius: 2,
            bgcolor: `${color}15`,
            border: `1px solid ${color}40`,
          }}
        >
          <ConnectWithoutContact sx={{ color: color, mr: 1, fontSize: "1rem" }} />
          <Typography variant="caption" sx={{ color: textColor, fontWeight: 600 }}>
            {network}
          </Typography>
        </Box>
      </CardContent>
    </Card>
    );
  };

  const NetworkStatCard = ({ value, label, icon, color }) => (
    <motion.div variants={bounceIn}>
      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          textAlign: "center",
          borderRadius: 4,
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          position: "relative",
          overflow: "hidden",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
            borderColor: theme.palette.primary.main,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: -20,
            right: -20,
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: `${color}15`,
          }
        }}
      >
        <Box sx={{ color: color, mb: 2 }}>{icon}</Box>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            color: theme.palette.text.primary,
            mb: 1,
            fontSize: { xs: "2rem", md: "2.5rem" },
          }}
        >
          {value}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: { xs: "0.875rem", md: "1rem" } }}>
          {label}
        </Typography>
      </Paper>
    </motion.div>
  );

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, overflow: "hidden" }}>
      <HomeHeader />
      <Toolbar />

      {/* Network-Focused Hero */}
      <Box
        component="section"
        ref={el => (refs.current['hero'] = el)}
        sx={{
          py: { xs: 8, md: 12 },
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><defs><pattern id=\"grain\" width=\"100\" height=\"100\" patternUnits=\"userSpaceOnUse\"><circle cx=\"25\" cy=\"25\" r=\"1\" fill=\"%23ffffff\" opacity=\"0.1\"/><circle cx=\"75\" cy=\"75\" r=\"1\" fill=\"%23ffffff\" opacity=\"0.1\"/><circle cx=\"50\" cy=\"10\" r=\"0.5\" fill=\"%23ffffff\" opacity=\"0.1\"/></pattern></defs><rect width=\"100\" height=\"100\" fill=\"url(%23grain)\"/></svg>') repeat",
            opacity: 0.3,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container alignItems="center" spacing={{ xs: 4, md: 6 }}>
            {/* Left side - Text content */}
            <Grid item xs={12} lg={6}>
              <Box sx={{ textAlign: { xs: "center", lg: "left" } }}>
                <motion.div variants={slideInNetwork} initial="hidden" animate="visible">
                  <Box sx={{ mb: 2, display: "inline-block", px: 2, py: 0.5, bgcolor:'#fff', borderRadius: 2 }}>
                    
                  <Typography>⚡ BLITZ COLLABORATE NETWORK</Typography>
                  </Box>
                  <Typography
                    variant="h1"
                    sx={{
                      mb: 3,
                      fontFamily: "Moret Variable",
                      fontWeight: 500,
                      fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem", lg: "4rem" },
                      lineHeight: 1.1,
                      color: theme.palette.text.primary,
                    }}
                  >
                    Connect. Create.
                    <br />
                    Collaborate.
                  </Typography>
                </motion.div>
                <motion.div variants={slideInNetwork} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 4,
                      fontWeight: 400,
                      opacity: 0.9,
                      maxWidth: { xs: "100%", lg: 500 },
                      mx: { xs: "auto", lg: 0 },
                      fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                      lineHeight: 1.7,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Build powerful partnerships in the creator economy with intelligent matching, seamless workflows, and instant collaboration tools
                  </Typography>
                </motion.div>

                {/* Network indicators */}
                <motion.div variants={slideInNetwork} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
                  <Box sx={{ mb: 4, display: "flex", justifyContent: { xs: "center", lg: "flex-start" } }}>
                    <Stack direction="row" spacing={{ xs: 2, sm: 3 }} flexWrap="wrap" useFlexGap>
                      {networkConnections.map((connection, index) => (
                        <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                          <Badge
                            badgeContent={connection.count}
                            sx={{
                              "& .MuiBadge-badge": {
                                bgcolor: connection.color,
                                color: "#fff",
                                fontWeight: 600,
                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              }
                            }}
                          >
                            <Hub sx={{ color: connection.color, fontSize: { xs: "1.25rem", sm: "1.5rem" } }} />
                          </Badge>
                          <Typography variant="body2" sx={{ ml: 2, fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                            {connection.type}s
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </motion.div>

                <motion.div variants={slideInNetwork} initial="hidden" animate="visible" transition={{ delay: 0.4 }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ justifyContent: { xs: "center", lg: "flex-start" } }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleNavigate}
                      sx={{
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        px: { xs: 4, md: 5 },
                        py: { xs: 1.5, md: 1.75 },
                        fontWeight: 700,
                        borderRadius: "50px",
                        textTransform: "none",
                        boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                        position: "relative",
                        overflow: "hidden",
                        '&::before': {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                          transition: "left 0.5s",
                        },
                        '&:hover': {
                          bgcolor: theme.palette.primary.light,
                          transform: 'translateY(-4px) scale(1.02)',
                          boxShadow: '0 16px 56px rgba(0,0,0,0.2)',
                          '&::before': {
                            left: '100%'
                          }
                        },
                        transition: "all 0.3s ease"
                      }}
                    >
                      <span style={{ position: "relative", zIndex: 1 }}>
                        Join the Network
                      </span>
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={handleNavigate1}
                      sx={{
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        px: { xs: 3, md: 4 },
                        py: { xs: 1.5, md: 1.75 },
                        fontWeight: 600,
                        borderRadius: "50px",
                        textTransform: "none",
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        borderWidth: 2,
                        "&:hover": {
                          borderWidth: 2,
                          bgcolor: `${theme.palette.primary.main}15`,
                          borderColor: theme.palette.primary.light,
                        }
                      }}
                    >
                      Explore Platform
                    </Button>
                  </Stack>
                </motion.div>
              </Box>
            </Grid>

            {/* Right side - Hero Image */}
            <Grid item xs={12} lg={6} sx={{ display: { xs: "none", md: "block" } }}>
              <motion.div
                variants={bounceIn}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: { xs: "16px", md: "24px" },
                    overflow: "hidden",
                    // boxShadow: "0 25px 80px rgba(0,0,0,0.15)",
                    transform: { lg: "perspective(1000px) rotateY(-5deg)" },
                    transition: "transform 0.4s ease",
                    "&:hover": {
                      transform: { lg: "perspective(1000px) rotateY(0deg) scale(1.02)" },
                    },
                  }}
                >
                  <img
                    src={collaborateImage}
                    alt="Collaboration Network"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      borderRadius: 'inherit',
                    }}
                  />
                  {/* Subtle gradient overlay */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Network Stats */}
      <Box sx={{ py: { xs: 6, md: 8 },  }}>
        <Container maxWidth="lg">
          <motion.div variants={fadeInStagger} initial="hidden" animate="visible">
            <Typography
              variant="h4"
              align="center"
              sx={{
                fontWeight: 700,
                mb: 6,
        color: theme.palette.text.primary,
        fontFamily: "Moret Variable",
        fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              }}
            >
              Growing Network Impact
            </Typography>
          </motion.div>
          
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <NetworkStatCard
                  value={stat.value}
                  label={stat.label}
                  icon={stat.icon}
                  color={stat.color}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Collaboration Modules */}
      <Box
        id="modules"
        ref={el => (refs.current['modules'] = el)}
        sx={{ py: { xs: 8, md: 10 }, bgcolor: theme.palette.background.default }}
      >
        <Container maxWidth="lg">
          <motion.div variants={fadeInStagger} initial="hidden" animate="visible">
            <Box sx={{ textAlign: "center", mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  color: theme.palette.text.primary,
                  fontFamily: "Moret Variable",
                  fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
                }}
              >
                Collaboration Ecosystem
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                  maxWidth: 600,
                  mx: "auto"
                }}
              >
                Integrated tools that power meaningful partnerships and drive results
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={4}>
            {modules.map((mod, idx) => (
              <Grid item xs={12} sm={6} lg={idx < 3 ? 4 : 6} key={idx}>
                <NetworkModuleCard
                  icon={mod.icon}
                  title={mod.title}
                  description={mod.description}
                  category={mod.category}
                  network={mod.network}
                  color={mod.color}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Collaboration Flow */}
      <Box
        id="how-it-works"
        ref={el => (refs.current['how-it-works'] = el)}
        sx={{
          py: { xs: 8, md: 10 },
          position: "relative",
          
        }}
      >
        <Container maxWidth="lg">
          <motion.div variants={fadeInStagger} initial="hidden" animate="visible">
            <Typography
              variant="h2"
              align="center"
              sx={{
                fontWeight: 800,
                mb: 8,
                fontFamily: "Moret Variable",
                fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
                color: theme.palette.text.primary,
              }}
            >
              Collaboration in Action
            </Typography>
          </motion.div>

          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} lg={6}>
              <motion.div variants={slideInNetwork} initial="hidden" animate="visible">
                <Stack spacing={{ xs: 2, md: 3 }}>
                  {[
                    {
                      icon: <Flag sx={{ color: "#FF6B35" }} />,
                      title: "Campaign Discovery",
                      description: "Set objectives, budgets, deliverables, and track status in one collaborative workspace",
                      connections: "Brand → Platform → Creator"
                    },
                    {
                      icon: <Search sx={{ color: "#F7931E" }} />,
                      title: "Smart Matching",
                      description: "AI-powered creator discovery based on audience, engagement, and brand alignment",
                      connections: "Algorithm ↔ Network Data"
                    },
                    {
                      icon: <Star sx={{ color: "#FFD23F" }} />,
                      title: "Partnership Building",
                      description: "Elevate relationships from one-off projects to strategic long-term partnerships",
                      connections: "Growing Network Effects"
                    },
                    {
                      icon: <Chat sx={{ color: "#06FFA5" }} />,
                      title: "Real-time Collaboration",
                      description: "Negotiate, approve, and manage projects through integrated communication flows",
                      connections: "Live Collaboration Hub"
                    },
                    {
                      icon: <Paid sx={{ color: "#3BCEAC" }} />,
                      title: "Instant Value Exchange",
                      description: "Trigger instant payouts and rewards upon milestone completion and approval",
                      connections: "Value → Creator Network"
                    }
                  ].map((step, index) => (
                    <Paper
                      key={index}
                      sx={{
                        p: { xs: 2.5, md: 3 },
                        borderRadius: 3,
                        background: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateX(8px)",
                          borderColor: theme.palette.primary.main,
                          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                        }
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${theme.palette.action.hover}, ${theme.palette.action.selected})`,
                            mr: 2,
                            mt: 0.5,
                          }}
                        >
                          {step.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: theme.palette.text.primary, fontSize: { xs: "1rem", md: "1.125rem" } }}>
                            {step.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1.5, lineHeight: 1.6 }}>
                            {step.description}
                          </Typography>
                          <Chip
                            label={step.connections}
                            size="small"
                            sx={{
                              bgcolor: `${theme.palette.primary.main}15`,
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} lg={6}>
              <motion.div variants={bounceIn} initial="hidden" animate="visible">
                <Paper
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: 4,
                    background: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.06)",
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, textAlign: "center", color: theme.palette.text.primary }}>
                    Platform Comparison
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                          Feature
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.text.secondary, textAlign: "center" }}>
                          Traditional
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main, textAlign: "center" }}>
                          Collaborate
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {[
                    { feature: "Setup Time", traditional: "Days", collaborate: "Minutes" },
                    { feature: "Network Visibility", traditional: "Limited", collaborate: "Full Ecosystem" },
                    { feature: "Communication", traditional: "Email/Calls", collaborate: "Integrated Hub" },
                    { feature: "Payment Speed", traditional: "Net Terms", collaborate: "Instant via BlitzPay" },
                    { feature: "Partnership Growth", traditional: "Manual", collaborate: "AI-Powered Matching" }
                  ].map((row, index) => (
                    <Box
                      key={index}
                      sx={{
                        py: 2,
                        px: 1,
                        borderRadius: 2,
                        bgcolor: index % 2 === 0 ? theme.palette.action.hover : "transparent",
                        mb: 1,
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                            {row.feature}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" sx={{ textAlign: "center", color: theme.palette.text.secondary, fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                            {row.traditional}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography
                            variant="body2"
                            sx={{
                              textAlign: "center",
                              color: theme.palette.primary.main,
                              fontWeight: 700,
                              fontSize: { xs: "0.75rem", md: "0.875rem" },
                            }}
                          >
                            {row.collaborate}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Network CTA */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          
          position: "relative",
        }}
      >
        <Container maxWidth="md">
          <motion.div variants={bounceIn} initial="hidden" animate="visible">
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ mb: 4 }}>
                <AvatarGroup max={6} sx={{ justifyContent: "center", mb: 3 }}>
                  {[...Array(8)].map((_, i) => (
                    <Avatar
                      key={i}
                      sx={{
                        bgcolor: ["#FF6B35", "#F7931E", "#FFD23F", "#06FFA5"][i % 4],
                        width: 50,
                        height: 50,
                        border: "3px solid #fff",
                        fontSize: "1.2rem",
                        fontWeight: 700,
                      }}
                    >
                      {String.fromCharCode(65 + i)}
                    </Avatar>
                  ))}
                </AvatarGroup>
              </Box>
              
              <Typography
                variant={isMobile ? "h3" : "h2"}
                sx={{
                  fontWeight: 900,
                  mb: 3,
                  color: theme.palette.text.primary,
                  fontFamily: "Moret Variable",
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "4rem" },
                }}
              >
                Ready to Amplify Your Network?
              </Typography>
              
              <Typography
                variant="h6"
                sx={{
                  mb: 6,
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                  maxWidth: 500,
                  mx: "auto"
                }}
              >
                Join thousands of brands and creators building the future of collaborative content
              </Typography>
              
              <Button
                variant="contained"
                size="large"
                onClick={handleNavigate}
                sx={{
                  fontSize: { xs: "1rem", md: "1.125rem" },
                  px: { xs: 5, md: 8 },
                  py: { xs: 2, md: 2.5 },
                  fontWeight: 700,
                  borderRadius: "50px",
                  textTransform: "none",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                  position: "relative",
                  overflow: "hidden",
                  '&::before': {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                    transition: "left 0.5s",
                  },
                  '&:hover': {
                    bgcolor: theme.palette.primary.light,
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: '0 16px 56px rgba(0,0,0,0.2)',
                    '&::before': {
                      left: '100%'
                    }
                  },
                  transition: "all 0.3s ease"
                }}
              >
                <span style={{ position: "relative", zIndex: 1 }}>
                  Connect & Collaborate
                </span>
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      <HomeFooter />

      <style jsx>{`
        @keyframes orbit {
          0% { transform: rotate(0deg) translateX(30px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(30px) rotate(-360deg); }
        }
      `}</style>
    </Box>
  );
}